/**
 * Download chips for PDF / Excel attachments on classified Graph mail feed
 * events. Inline signature images are filtered out.
 */
import { useState } from "react";
import { Download, Loader2 } from "lucide-react";

import { isGraphMailDocumentFilename } from "@/lib/graphMailDocuments";
import { cn } from "@/lib/utils";
import { api } from "@/services/api";
import type { GraphMailAttachmentRef } from "@/types/feed";

function AttachmentChip({ attachment }: { attachment: GraphMailAttachmentRef }) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async () => {
    setDownloading(true);
    setError(null);
    try {
      await api.downloadGraphAttachment(
        attachment.mailbox_id,
        attachment.message_id,
        attachment.attachment_id,
        attachment.filename,
      );
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to download attachment";
      console.warn("Failed to download Graph mail attachment:", err);
      setError(message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      title={error ?? `Download ${attachment.filename}`}
      onClick={(e) => {
        e.stopPropagation();
        void handleClick();
      }}
      disabled={downloading}
      className={cn(
        "inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/20",
        downloading && "opacity-60",
        error && "bg-destructive/10 text-destructive hover:bg-destructive/20",
      )}
    >
      {downloading ? (
        <Loader2 className="size-2.5 animate-spin" />
      ) : (
        <Download className="size-2.5" />
      )}
      <span className="max-w-[14rem] truncate">{attachment.filename}</span>
    </button>
  );
}

export function GraphMailAttachmentDownloads({
  attachments,
}: {
  attachments: GraphMailAttachmentRef[];
}) {
  const docs = attachments.filter((att) =>
    isGraphMailDocumentFilename(att.filename),
  );
  if (docs.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1">
      {docs.map((att) => (
        <AttachmentChip key={att.attachment_id} attachment={att} />
      ))}
    </div>
  );
}
